<script setup lang="ts">
import { ref } from 'vue'
import { Copy, CheckCircle2, Clock, XCircle, AlertCircle, ArrowRight, ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-vue-next'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { API, HOST } from '@/utils/http'
import { useToast } from '@/components/ui/toast/use-toast'
import * as Sentry from '@sentry/vue'
import type { BridgeTransfer, BridgeTransferStatus } from '@/types/bridge'

const props = defineProps<{
  transfers: BridgeTransfer[]
  loading: boolean
  allowCancel?: boolean
}>()

const emit = defineEmits<{ (e: 'transfer-cancelled', transfer: BridgeTransfer): void }>()

const { toast } = useToast()

const confirmCancelDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingCancel = ref<BridgeTransfer | null>(null)
const cancelling = ref<Set<number>>(new Set())

const requestCancel = (transfer: BridgeTransfer) => {
  pendingCancel.value = transfer
  confirmCancelDialog.value?.open()
}

const confirmCancel = async () => {
  const transfer = pendingCancel.value
  if (!transfer) return
  cancelling.value.add(transfer.id)
  try {
    const response = await API.delete(`${HOST}/payments/bridge/transfers/${transfer.id}`)
    toast({ title: 'Transfer cancelled' })
    emit('transfer-cancelled', response.data)
  } catch (err: any) {
    const msg = err.response?.data?.detail || 'Failed to cancel transfer'
    toast({ title: 'Cancel failed', description: msg, variant: 'destructive' })
    Sentry.captureException(err, { extra: { transfer_id: transfer.id } })
  } finally {
    cancelling.value.delete(transfer.id)
    pendingCancel.value = null
  }
}

// Track expanded rows
const expandedRows = ref<Set<number>>(new Set())

const toggleRow = (transferId: number) => {
  if (expandedRows.value.has(transferId)) {
    expandedRows.value.delete(transferId)
  } else {
    expandedRows.value.add(transferId)
  }
}

const isExpanded = (transferId: number) => {
  return expandedRows.value.has(transferId)
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
}

const getStatusColor = (status: BridgeTransferStatus): string => {
  switch (status) {
    case 'payment_processed':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'pending':
    case 'awaiting_funds':
      return 'bg-amber-100 text-amber-700 border-amber-200'
    case 'funds_received':
    case 'payment_submitted':
    case 'in_review':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'undeliverable':
      return 'bg-red-100 text-red-700 border-red-200'
    default:
      return 'bg-muted text-muted-foreground border-border'
  }
}

const getStatusIcon = (status: BridgeTransferStatus) => {
  switch (status) {
    case 'payment_processed':
      return CheckCircle2
    case 'pending':
    case 'awaiting_funds':
    case 'funds_received':
    case 'payment_submitted':
    case 'in_review':
      return Clock
    case 'undeliverable':
      return XCircle
    default:
      return AlertCircle
  }
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatAmount = (amount: string, currency: string | null | undefined) => {
  const num = parseFloat(amount)
  if (isNaN(num)) return '—'
  const code = (currency || 'USD').toUpperCase()
  // Intl.NumberFormat requires ISO 4217; USDB/USDC are USD-pegged stablecoins
  const isoCode = (code === 'USDB' || code === 'USDC') ? 'USD' : code
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: isoCode }).format(num)
  } catch {
    return `${num.toFixed(2)} ${code}`
  }
}

const formatStatusLabel = (status: string) => {
  return status.replace(/_/g, ' ')
}

const getTypeLabel = (type: string) => {
  if (type === 'onramp') return 'On-ramp'
  if (type === 'offramp') return 'Off-ramp'
  return 'Transfer'
}

const getTypeColor = (type: string) => {
  if (type === 'onramp') return 'bg-emerald-100 text-emerald-700'
  if (type === 'offramp') return 'bg-orange-100 text-orange-700'
  return 'bg-blue-100 text-blue-700'
}

const getFromLabel = (transfer: BridgeTransfer) => {
  if (transfer.transaction_type === 'onramp') return transfer.sender_name || 'External'
  return transfer.sender_display_name || transfer.sender_email || 'Unknown'
}

const formatRail = (rail: string | null | undefined): string => {
  if (!rail) return 'ACH'
  const r = rail.toLowerCase()
  if (r === 'ach' || r === 'ach_push') return 'ACH'
  if (r === 'wire') return 'Wire'
  return rail.toUpperCase()
}

const getToLabel = (transfer: BridgeTransfer) => {
  if (transfer.transaction_type === 'onramp') return transfer.sender_display_name || transfer.sender_email || 'Org'
  if (transfer.transaction_type === 'offramp') {
    return transfer.destination_account_label || formatRail(transfer.destination_payment_rail)
  }
  // Fallback: if destination rail looks fiat, treat as untagged offramp
  const rail = transfer.destination_payment_rail?.toLowerCase()
  if (rail === 'ach' || rail === 'ach_push' || rail === 'wire') {
    return transfer.destination_account_label || formatRail(transfer.destination_payment_rail)
  }
  return transfer.receiver_display_name || transfer.receiver_email || 'Unknown'
}
</script>

<template>
  <div class="h-full">
    <!-- Loading state -->
    <div v-if="loading" class="p-4">
      <div class="space-y-3">
        <div v-for="i in 5" :key="i" class="flex items-center space-x-3 animate-pulse">
          <div class="h-10 w-10 bg-muted rounded-full"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-muted rounded w-1/4"></div>
            <div class="h-4 bg-muted rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="transfers.length === 0" class="border rounded-md p-8 text-center text-muted-foreground">
      No transfers yet.
    </div>

    <!-- Transfers table -->
    <div v-else class="border rounded-md">
      <table class="w-full">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="h-10 w-8 px-1 text-left align-middle font-medium text-muted-foreground"></th>
            <th class="h-10 px-3 text-left align-middle font-medium text-muted-foreground">Type</th>
            <th class="h-10 px-3 text-left align-middle font-medium text-muted-foreground">From / To</th>
            <th class="h-10 px-3 text-right align-middle font-medium text-muted-foreground">Amount</th>
            <th class="h-10 px-3 text-center align-middle font-medium text-muted-foreground">Status</th>
            <th class="h-10 px-3 text-left align-middle font-medium text-muted-foreground">Created</th>
            <th v-if="allowCancel" class="h-10 w-10"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="transfer in transfers" :key="transfer.id">
            <!-- Main row -->
            <tr
              class="border-b hover:bg-muted/50 cursor-pointer"
              @click="toggleRow(transfer.id)"
            >
              <!-- Expand toggle -->
              <td class="py-2 px-1 align-middle text-center">
                <ChevronDown v-if="isExpanded(transfer.id)" class="h-4 w-4 text-muted-foreground" />
                <ChevronRight v-else class="h-4 w-4 text-muted-foreground" />
              </td>

              <!-- Type -->
              <td class="py-2 px-3 align-middle">
                <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium" :class="getTypeColor(transfer.transaction_type)">
                  {{ getTypeLabel(transfer.transaction_type) }}
                </span>
              </td>

              <!-- From / To -->
              <td class="py-2 px-3 align-middle">
                <div class="flex items-center gap-1">
                  <div class="min-w-0">
                    <div class="font-medium text-sm truncate">{{ getFromLabel(transfer) }}</div>
                  </div>
                  <ArrowRight class="h-3 w-3 text-muted-foreground flex-shrink-0 mx-1" />
                  <div class="min-w-0">
                    <div class="font-medium text-sm truncate">{{ getToLabel(transfer) }}</div>
                  </div>
                </div>
              </td>

              <!-- Amount -->
              <td class="py-2 px-3 align-middle text-right">
                <div class="font-medium">{{ formatAmount(transfer.amount, transfer.source_currency) }}</div>
              </td>

              <!-- Status -->
              <td class="py-2 px-3 align-middle text-center">
                <Badge :class="getStatusColor(transfer.status)" class="capitalize">
                  <component :is="getStatusIcon(transfer.status)" class="h-3 w-3 mr-1" />
                  {{ formatStatusLabel(transfer.status) }}
                </Badge>
              </td>

              <!-- Created -->
              <td class="py-2 px-3 align-middle text-sm text-muted-foreground whitespace-nowrap">
                {{ formatDate(transfer.created_at) }}
              </td>

              <!-- Actions -->
              <td v-if="allowCancel" class="py-2 pr-2 align-middle text-right" @click.stop>
                <DropdownMenu v-if="transfer.status === 'awaiting_funds'">
                  <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="sm" class="h-7 w-7 p-0" :disabled="cancelling.has(transfer.id)">
                      <MoreHorizontal class="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      @click="requestCancel(transfer)"
                      class="text-destructive focus:text-destructive"
                    >
                      Cancel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>

            <!-- Expanded details row -->
            <tr v-if="isExpanded(transfer.id)" class="bg-muted/30">
              <td :colspan="allowCancel ? 7 : 6" class="p-4">
                <div class="pl-6 space-y-2 text-sm">
                  <div class="grid grid-cols-2 gap-4 max-w-2xl">
                    <!-- Bridge Transfer ID -->
                    <div>
                      <span class="text-muted-foreground">Bridge Transfer ID:</span>
                      <div class="mt-1">
                        <button
                          @click.stop="copyToClipboard(transfer.bridge_transfer_id)"
                          class="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 text-xs font-mono"
                        >
                          <span>{{ transfer.bridge_transfer_id }}</span>
                          <Copy class="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    </div>

                    <!-- Internal ID -->
                    <div>
                      <span class="text-muted-foreground">Internal ID:</span>
                      <div class="mt-1 text-xs font-mono">{{ transfer.id }}</div>
                    </div>

                    <!-- Payment Rails -->
                    <div>
                      <span class="text-muted-foreground">Payment Rails:</span>
                      <div class="mt-1">
                        <span class="text-xs">{{ transfer.source_payment_rail }}</span>
                        <span class="text-xs text-muted-foreground mx-1">→</span>
                        <span class="text-xs">{{ transfer.destination_payment_rail }}</span>
                      </div>
                    </div>

                    <!-- Currency -->
                    <div>
                      <span class="text-muted-foreground">Currency:</span>
                      <div class="mt-1">
                        <span class="text-xs uppercase">{{ transfer.source_currency }}</span>
                        <span class="text-xs text-muted-foreground mx-1">→</span>
                        <span class="text-xs uppercase">{{ transfer.destination_currency }}</span>
                      </div>
                    </div>

                    <!-- Sender Customer ID -->
                    <div>
                      <span class="text-muted-foreground">Sender Customer ID:</span>
                      <div class="mt-1 text-xs font-mono">{{ transfer.sender_customer_id }}</div>
                    </div>

                    <!-- Receiver Customer ID -->
                    <div>
                      <span class="text-muted-foreground">Receiver Customer ID:</span>
                      <div class="mt-1 text-xs font-mono">{{ transfer.receiver_customer_id }}</div>
                    </div>

                    <!-- Updated At -->
                    <div>
                      <span class="text-muted-foreground">Updated:</span>
                      <div class="mt-1 text-xs">{{ formatDate(transfer.updated_at) }}</div>
                    </div>

                    <!-- Error Message (if any) -->
                    <div v-if="transfer.error_message">
                      <span class="text-muted-foreground">Error:</span>
                      <div class="mt-1 text-xs text-destructive">{{ transfer.error_message }}</div>
                    </div>
                  </div>

                  <!-- Deposit Instructions (if available) -->
                  <div v-if="transfer.source_deposit_instructions" class="mt-4 pt-4 border-t">
                    <span class="text-muted-foreground font-medium">Deposit Instructions:</span>
                    <div class="mt-2 grid grid-cols-2 gap-4 max-w-2xl">
                      <div v-if="transfer.source_deposit_instructions.deposit_message">
                        <span class="text-muted-foreground">Reference Code:</span>
                        <div class="mt-1">
                          <button
                            @click.stop="copyToClipboard(transfer.source_deposit_instructions.deposit_message!)"
                            class="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 text-xs font-mono font-bold text-primary"
                          >
                            <span>{{ transfer.source_deposit_instructions.deposit_message }}</span>
                            <Copy class="h-3 w-3 text-muted-foreground" />
                          </button>
                        </div>
                      </div>
                      <div v-if="transfer.source_deposit_instructions.amount">
                        <span class="text-muted-foreground">Deposit Amount:</span>
                        <div class="mt-1 text-xs">
                          {{ transfer.source_deposit_instructions.currency }} {{ transfer.source_deposit_instructions.amount }}
                        </div>
                      </div>
                      <div v-if="transfer.source_deposit_instructions.bank_name">
                        <span class="text-muted-foreground">Bank:</span>
                        <div class="mt-1 text-xs">{{ transfer.source_deposit_instructions.bank_name }}</div>
                      </div>
                      <div v-if="transfer.source_deposit_instructions.bank_account_number">
                        <span class="text-muted-foreground">Account Number:</span>
                        <div class="mt-1">
                          <button
                            @click.stop="copyToClipboard(transfer.source_deposit_instructions.bank_account_number!)"
                            class="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 text-xs font-mono"
                          >
                            <span>{{ transfer.source_deposit_instructions.bank_account_number }}</span>
                            <Copy class="h-3 w-3 text-muted-foreground" />
                          </button>
                        </div>
                      </div>
                      <div v-if="transfer.source_deposit_instructions.bank_routing_number">
                        <span class="text-muted-foreground">Routing Number:</span>
                        <div class="mt-1">
                          <button
                            @click.stop="copyToClipboard(transfer.source_deposit_instructions.bank_routing_number!)"
                            class="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 text-xs font-mono"
                          >
                            <span>{{ transfer.source_deposit_instructions.bank_routing_number }}</span>
                            <Copy class="h-3 w-3 text-muted-foreground" />
                          </button>
                        </div>
                      </div>
                      <div v-if="transfer.source_deposit_instructions.bank_beneficiary_name">
                        <span class="text-muted-foreground">Beneficiary:</span>
                        <div class="mt-1 text-xs">{{ transfer.source_deposit_instructions.bank_beneficiary_name }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>

  <DialogConfirm
    ref="confirmCancelDialog"
    title="Cancel transfer"
    :description="pendingCancel ? `Cancel the ${formatAmount(pendingCancel.amount, pendingCancel.source_currency)} awaiting transfer? This cannot be undone.` : ''"
    confirm-text="Cancel transfer"
    variant="error"
    @confirm="confirmCancel"
  />
</template>
